// Pending answers are never discarded on a network error. Only a confirmed
// response, or an explicit Undo of an answer that has not been sent, removes one.
// Full media/boxes are bounded to the latest answer and active restoration;
// queued jobs keep compact identities rather than duplicating every clip.
const sessions = new Map();
const localLocks = new Map();
const transientCodes = new Set(['unavailable', 'deadline-exceeded', 'internal', 'unknown', 'cancelled', 'resource-exhausted']);
const reconciledReasons = new Set(['task-closed', 'task-missing', 'task-unassigned', 'submission-undone']);
const uuid = () => window.crypto?.randomUUID?.() || `motion-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
const clone = (value) => JSON.parse(JSON.stringify(value));
const delay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));
const failure = (code, message) => Object.assign(new Error(message), { code });
const isTransient = (error) => !error?.code || transientCodes.has(String(error.code).replace(/^functions\//, ''));
const messageFor = (error) => error?.message || 'This review could not sync. It is still saved on this device.';
const emptyState = (scope) => ({ version: 1, reviewCountVersion: 2, instantUndoVersion: 1, scope, jobs: [], latest: null, lastSubmission: null, reviewedCount: null, undo: null, lastUndo: null, restoration: null });
const countedAnswer = (answer) => answer?.verdict !== 'unsure';
const countedReceipt = (receipt) => receipt?.counted !== false;
const compactTask = (task) => Object.fromEntries(['id', 'cameraLabel', 'capturedAt', 'frameW', 'frameH'].filter((key) => task?.[key] !== undefined).map((key) => [key, task[key]]));
const taskSnapshot = (task) => {
  const result = clone(task);
  if (['videoUrl', 'maskVideoUrl', 'fullClipUrl'].some((field) => String(result[field] || '').startsWith('blob:'))) {
    throw failure('motion/outbox-task', 'The original clip location is required to keep this review available after a reload.');
  }
  return result;
};

export function createMotionReviewOutbox(options) {
  if (!options?.scope) throw failure('motion/outbox-scope', 'Sign in before saving a motion review.');
  const scope = String(options.scope);
  const key = `ting-vision:motion-outbox:v1:${encodeURIComponent(scope)}`;
  let settings = { ...options };
  // Access the browser storage property inside guarded operations: browsers
  // can throw SecurityError merely while retrieving window.localStorage.
  const storage = {
    getItem: (itemKey) => (options.storage || window.localStorage).getItem(itemKey),
    setItem: (itemKey, value) => (options.storage || window.localStorage).setItem(itemKey, value),
    removeItem: (itemKey) => (options.storage || window.localStorage).removeItem(itemKey),
  };
  const locks = options.locks || navigator?.locks;
  const owner = uuid();
  const listeners = new Set();
  let state = emptyState(scope);
  let snapshot;
  let storageError = null;
  let runtimeError = null;
  let timer;
  let pumping = null;
  let disposed = false;

  const active = () => !disposed && (settings.isActive ? Boolean(settings.isActive()) : true);
  const assertActive = () => { if (!active()) throw failure('motion/outbox-inactive', 'Sign in with the account that saved this review to continue syncing.'); };
  const online = () => navigator?.onLine !== false;
  const safelyCall = (callback, ...args) => {
    try { Promise.resolve(callback?.(...args)).catch(() => {}); } catch (_) { /* UI callbacks cannot change an already durable answer. */ }
  };
  const publish = () => {
    const blocked = state.undo?.error || state.jobs.find((job) => job.error)?.error;
    const undoError = state.undo?.error || state.jobs.find((job) => job.requestId === state.undo?.targetRequestId)?.error;
    const deniedUndo = Boolean(state.undo && (state.undo.undoCompleted || (state.undo.status === 'blocked' && state.undo.definitive)));
    const dependentCount = deniedUndo ? state.jobs.filter((job) => job.dependsOnUndoRequestId === state.undo.requestId && countedAnswer(job.answer)).length : 0;
    const previousTask = state.restoration?.task || state.latest?.task || state.undo?.task || null;
    snapshot = {
      pendingCount: state.jobs.length, pendingReviewedCount: state.jobs.filter((job) => countedAnswer(job.answer)).length, jobs: clone(state.jobs), latest: clone(state.latest),
      lastSubmission: clone(state.lastSubmission), reviewedCount: state.reviewedCount,
      lastUndo: clone(state.lastUndo),
      previousTask: clone(previousTask), restoreTask: clone(state.restoration?.task || null), restoreRequestId: state.restoration?.requestId || null,
      undoPending: Boolean(state.undo), optimisticUndo: Boolean(state.undo && !deniedUndo),
      countDelta: state.undo && !deniedUndo ? (state.undo.counted === false ? 0 : -1) : dependentCount ? -dependentCount : 0,
      canUndo: Boolean(previousTask?.videoUrl && (state.restoration || state.undo || state.jobs.length || state.lastSubmission)),
      error: runtimeError || blocked || null, storageError, undoing: Boolean(state.undo && state.undo.status !== 'blocked' && !undoError && online() && active()),
      canCancelUndo: Boolean(state.undo?.status === 'blocked' && state.undo.errorCode === 'failed-precondition' && state.undo.definitive),
      // Web Locks provide atomic coordination. The lease fallback is best
      // effort across tabs because localStorage has no compare-and-swap API.
      locking: locks?.request ? 'web-locks' : 'storage-lease',
    };
    for (const listener of listeners) safelyCall(listener, snapshot);
    safelyCall(settings.onStatus, snapshot);
  };
  const storageFailure = (error) => {
    storageError = 'This device could not save the review queue. Keep this page open and free some browser storage, then retry.';
    publish();
    return failure('motion/outbox-storage', `${storageError}${error?.message ? ` (${error.message})` : ''}`);
  };
  const read = () => {
    try {
      const raw = storage.getItem(key);
      const next = raw ? JSON.parse(raw) : emptyState(scope);
      if (next.version !== 1 || next.scope !== scope || !Array.isArray(next.jobs) || next.jobs.some((job) => !job.requestId || !job.answer?.taskId)) {
        throw new Error('The saved queue could not be read. Its contents have been preserved.');
      }
      // Refresh old all-answer totals after upgrading; preserve every queued
      // answer and Undo. Only the server can provide the corrected total.
      if (next.reviewCountVersion !== 2) { next.reviewedCount = null; next.reviewCountVersion = 2; }
      if (next.undo && next.undo.counted === undefined) {
        const target = next.jobs.find((job) => job.requestId === next.undo.targetRequestId) || (next.latest?.requestId === next.undo.targetRequestId ? next.latest : null);
        next.undo.counted = target?.answer ? countedAnswer(target.answer) : countedReceipt(next.lastSubmission);
      }
      if (!next.instantUndoVersion && next.undo && next.latest?.taskId === next.undo.taskId) {
        next.undo.task = next.latest.task;
        next.restoration = { requestId: next.undo.requestId, undoRequestId: next.undo.requestId, taskId: next.undo.taskId, task: next.latest.task, localOnly: false };
      }
      // Retry a saved failure from the old server once after upgrading. Keep
      // the original answer and request ID until the server confirms its fate.
      for (const job of next.jobs) {
        if (job.status !== 'blocked' || job.assignmentRecoveryVersion === 1 ||
            job.errorCode !== 'failed-precondition' ||
            job.error !== 'Load your review buffer before submitting this track.') continue;
        Object.assign(job, { status: 'queued', error: null, nextAttemptAt: 0, assignmentRecoveryVersion: 1 });
        if (next.latest?.requestId === job.requestId) next.latest.status = 'queued';
      }
      return { ...emptyState(scope), ...next };
    } catch (error) { throw storageFailure(error); }
  };
  const write = (next) => {
    try { storage.setItem(key, JSON.stringify(next)); }
    catch (error) { throw storageFailure(error); }
    state = next; storageError = null; publish();
  };

  // A short state lock covers only read/modify/write. A separate pump lock can
  // remain held over a slow request without making the next local save wait.
  const withLock = async (kind, callback) => {
    const name = `${key}:${kind}`;
    if (locks?.request) return locks.request(name, () => callback(() => true));
    const previous = localLocks.get(name) || Promise.resolve();
    let releaseLocal;
    const local = new Promise((resolve) => { releaseLocal = resolve; });
    const tail = previous.catch(() => {}).then(() => local);
    localLocks.set(name, tail);
    await previous.catch(() => {});
    const leaseKey = `${name}:lease`;
    const leaseOwner = `${owner}:${uuid()}`;
    const ttl = kind === 'pump' ? 30000 : 3000;
    let heartbeat;
    const owned = () => {
      try { return JSON.parse(storage.getItem(leaseKey) || 'null')?.owner === leaseOwner; }
      catch (_) { return false; }
    };
    try {
      while (true) {
        let lease;
        try {
          lease = JSON.parse(storage.getItem(leaseKey) || 'null');
          if (!lease || lease.expiresAt <= Date.now()) {
            storage.setItem(leaseKey, JSON.stringify({ owner: leaseOwner, expiresAt: Date.now() + ttl }));
            // Let a competing tab's claim become visible before entering.
            await delay(12);
            if (owned()) break;
          }
        } catch (error) { throw storageFailure(error); }
        if (disposed) throw failure('motion/outbox-inactive', 'The review queue is paused.');
        await delay(50);
      }
      heartbeat = setInterval(() => {
        try { if (owned()) storage.setItem(leaseKey, JSON.stringify({ owner: leaseOwner, expiresAt: Date.now() + ttl })); }
        catch (_) { /* Losing a lease prevents the next request; request IDs make an overlapping retry safe. */ }
      }, ttl / 3);
      return await callback(owned);
    } finally {
      clearInterval(heartbeat);
      try { if (owned()) storage.removeItem(leaseKey); } catch (_) { /* An expired lease is recoverable. */ }
      releaseLocal();
      if (localLocks.get(name) === tail) localLocks.delete(name);
    }
  };
  const mutate = (change) => withLock('state', (owned) => {
    const next = read();
    const result = change(next);
    if (!owned()) throw failure('motion/outbox-lock', 'Another tab is updating the saved queue. Please retry.');
    write(next);
    return result;
  });
  const refresh = () => {
    try { state = read(); storageError = null; publish(); }
    catch (_) { /* read already exposes the preserved queue's storage error. */ }
    return snapshot;
  };
  const schedule = (milliseconds = 0) => {
    clearTimeout(timer);
    if (!disposed) timer = setTimeout(() => { timer = undefined; void pump().catch(() => {}); }, milliseconds);
  };
  const responseProgress = (next, response) => {
    if (Number.isFinite(response.reviewedCount)) next.reviewedCount = response.reviewedCount;
    if (Object.prototype.hasOwnProperty.call(response, 'lastSubmission')) next.lastSubmission = response.lastSubmission || null;
  };
  const persistError = async (kind, requestId, error) => {
    const transient = isTransient(error);
    let retryIn = null;
    await mutate((next) => {
      const item = kind === 'undo' ? next.undo : next.jobs.find((job) => job.requestId === requestId);
      if (!item || item.requestId !== requestId) return;
      item.attempts = (item.attempts || 0) + 1;
      item.status = transient ? 'queued' : 'blocked';
      item.errorCode = error?.code ? String(error.code).replace(/^functions\//, '') : null;
      item.definitive = kind === 'undo' && item.errorCode === 'failed-precondition';
      item.error = transient ? 'A review is waiting to sync. Your answers are saved on this device.' : messageFor(error);
      retryIn = transient ? Math.min(30000, 1000 * (2 ** Math.min(10, item.attempts - 1))) : null;
      item.nextAttemptAt = retryIn === null ? null : Date.now() + retryIn;
      if (kind === 'submit' && next.latest?.requestId === requestId) next.latest.status = item.status;
    });
    if (retryIn !== null) schedule(retryIn);
  };

  const drain = async (owned) => {
    while (active() && owned() && !storageError) {
      if (!online()) { runtimeError = 'Offline. Your reviews are saved on this device and will sync when you reconnect.'; publish(); return; }
      runtimeError = null;
      state = read(); publish();
      // Undo is serialized ahead of later answers so the server still regards
      // its target as the most recent accepted review.
      if (state.undo?.status === 'blocked') return;
      if (state.undo && state.undo.submissionId) {
        const undo = state.undo;
        if (undo.status === 'blocked') return;
        if (undo.nextAttemptAt > Date.now()) { schedule(undo.nextAttemptAt - Date.now()); return; }
        try {
          assertActive(); if (!owned()) return;
          const response = await settings.api.undo({ taskId: undo.taskId, submissionId: undo.submissionId, requestId: undo.requestId });
          if (response?.undone !== true) throw failure('motion/outbox-unconfirmed', 'The server did not confirm Undo. The saved request has been kept for retry.');
          if (response.undoActive !== false && (response.undoActive !== true || typeof response.undoToken !== 'string' || !response.undoToken)) {
            throw failure('motion/outbox-unconfirmed', 'Undo was not accompanied by a current correction token. Your replacement answer is kept until its status can be confirmed.');
          }
          await mutate((next) => {
            if (next.undo?.requestId !== undo.requestId) return;
            responseProgress(next, response);
            if (response.undoActive === false) {
              Object.assign(next.undo, { status: 'blocked', undoCompleted: true, definitive: true, errorCode: 'failed-precondition', error: 'This Undo has been superseded or resolved. Its replacement answer has not been sent.' });
              return;
            }
            for (const job of next.jobs) {
              if (job.dependsOnUndoRequestId !== undo.requestId) continue;
              job.undoReady = true; job.answer.undoToken = response.undoToken;
              if (next.latest?.requestId === job.requestId) { next.latest.answer = job.answer; next.latest.task.undoToken = response.undoToken; }
            }
            // A correction must reach the server before later ordinary votes,
            // which could otherwise supersede its restored assignment.
            next.jobs = [...next.jobs.filter((job) => job.dependsOnUndoRequestId === undo.requestId), ...next.jobs.filter((job) => job.dependsOnUndoRequestId !== undo.requestId)];
            if (next.restoration?.undoRequestId === undo.requestId) next.restoration.task.undoToken = response.undoToken;
            if (next.latest?.requestId === undo.targetRequestId) next.latest = null;
            next.lastUndo = { requestId: undo.requestId, taskId: undo.taskId, response };
            next.undo = null;
          });
          if (response.undoActive === false) {
            safelyCall(settings.onReconcile, { taskId: undo.taskId }, { accepted: null, reason: 'undo-inactive', error: { code: 'functions/failed-precondition', message: 'The restored review is no longer active. Its replacement remains saved and has not been sent.' } });
            return;
          }
        } catch (error) {
          if (!active()) return;
          if (error.code === 'motion/outbox-inactive' || error.code === 'motion/outbox-storage') return;
          await persistError('undo', undo.requestId, error); return;
        }
        continue;
      }
      // Leave the restored assignment current while its clip is being reviewed.
      // Ordinary queued answers can resume once a replacement has been saved.
      if (!state.undo && state.restoration?.undoRequestId) return;
      const first = state.jobs[0];
      if (!first) return;
      if (first.dependsOnUndoRequestId && !first.undoReady) return;
      if (first.status === 'blocked') return;
      if (first.nextAttemptAt > Date.now()) { schedule(first.nextAttemptAt - Date.now()); return; }
      let job;
      await mutate((next) => {
        const candidate = next.jobs[0];
        if (!candidate || candidate.requestId !== first.requestId || candidate.status === 'blocked' || (candidate.dependsOnUndoRequestId && !candidate.undoReady)) return;
        candidate.status = 'sending'; candidate.everSent = true; candidate.error = null;
        if (next.latest?.requestId === candidate.requestId) next.latest.status = 'sending';
        job = clone(candidate);
      });
      if (!job) continue;
      try {
        assertActive(); if (!owned()) return;
        const response = await settings.api.submit(job.answer);
        if (response?.accepted !== true && !(response?.accepted === false && reconciledReasons.has(response.reason))) {
          throw failure('functions/failed-precondition', 'The server did not confirm this review. It is still saved on this device; retry to check its status.');
        }
        await mutate((next) => {
          next.jobs = next.jobs.filter((item) => item.requestId !== job.requestId);
          responseProgress(next, response);
          if (next.latest?.requestId === job.requestId) {
            if (response.accepted) { next.latest.status = 'acknowledged'; next.latest.receipt = response.lastSubmission?.taskId === job.taskId ? response.lastSubmission : null; }
            else next.latest = null;
          }
          if (next.undo?.targetRequestId === job.requestId) {
            if (response.accepted && response.lastSubmission?.taskId === job.taskId) {
              next.undo.counted = countedReceipt(response.lastSubmission);
              next.undo.submissionId = response.lastSubmission.submissionId; next.undo.status = 'queued';
            } else {
              next.undo.status = 'blocked'; next.undo.error = 'That answer is no longer your latest accepted review and could not be undone.';
              if (response.accepted === false || response.lastSubmission?.taskId) {
                next.undo.errorCode = 'failed-precondition'; next.undo.definitive = true;
              }
            }
          }
        });
        safelyCall(response.accepted ? settings.onAck : settings.onReconcile, job.answer, response);
      } catch (error) {
        if (!active()) return;
        if (error.code === 'motion/outbox-inactive' || error.code === 'motion/outbox-storage') return;
        await persistError('submit', job.requestId, error);
        if (!isTransient(error)) safelyCall(settings.onReconcile, job.answer, { accepted: null, reason: 'sync-error', error: { code: error.code, message: messageFor(error) } });
        return;
      }
    }
  };
  const pump = () => {
    if (pumping) return pumping;
    if (!active() || storageError) return Promise.resolve();
    pumping = withLock('pump', drain).catch((error) => {
      if (error.code !== 'motion/outbox-storage' && error.code !== 'motion/outbox-inactive') { runtimeError = messageFor(error); publish(); }
    }).finally(() => { pumping = null; });
    return pumping;
  };

  const submit = async (answer, task) => {
    assertActive();
    const requestId = answer?.requestId || uuid();
    const taskId = answer?.taskId || task?.id;
    if (!taskId || task?.id !== taskId) throw failure('motion/outbox-task', 'The review does not match its clip. Refresh the clip before saving.');
    const savedAnswer = clone({ ...answer, taskId, requestId });
    const savedTask = taskSnapshot(task);
    await mutate((next) => {
      assertActive();
      const existing = next.jobs.find((job) => job.requestId === requestId);
      if (existing) {
        if (JSON.stringify(existing.answer) !== JSON.stringify(savedAnswer)) throw failure('motion/outbox-request', 'This saved request already belongs to a different answer.');
        return;
      }
      if (next.latest?.requestId === requestId && next.latest.status === 'acknowledged') return;
      const pendingUndo = next.undo?.taskId === taskId ? next.undo : null;
      const restoration = next.restoration?.taskId === taskId ? next.restoration : null;
      const dependency = pendingUndo?.requestId || restoration?.undoRequestId || null;
      if (pendingUndo?.status === 'blocked' && pendingUndo.definitive) {
        throw failure('motion/outbox-undo', 'The previous answer could not be undone. Resolve that failed Undo before submitting a replacement.');
      }
      if (next.jobs.some((job) => job.taskId === taskId && (job.dependsOnUndoRequestId || null) === dependency)) {
        throw failure('motion/outbox-request', 'An answer for this clip is already saved. Use Undo before changing it.');
      }
      if (pendingUndo) { delete savedAnswer.undoToken; delete savedTask.undoToken; }
      else if (restoration?.task.undoToken) { savedAnswer.undoToken = restoration.task.undoToken; savedTask.undoToken = restoration.task.undoToken; }
      const job = { requestId, taskId, answer: savedAnswer, task: compactTask(task), queuedAt: Date.now(), status: 'queued', everSent: false, attempts: 0, error: null, nextAttemptAt: 0,
        ...(dependency ? { dependsOnUndoRequestId: dependency, undoReady: !pendingUndo && Boolean(savedAnswer.undoToken) } : {}) };
      if (dependency) {
        const targetIndex = pendingUndo ? next.jobs.findIndex((item) => item.requestId === pendingUndo.targetRequestId) : -1;
        next.jobs.splice(targetIndex + 1, 0, job);
      } else next.jobs.push(job);
      next.latest = { requestId, taskId, task: savedTask, answer: savedAnswer, status: 'queued', receipt: null };
      if (restoration) next.restoration = null;
    });
    schedule();
    return { requestId, taskId, answer: savedAnswer };
  };

  const undoLatest = async () => {
    assertActive();
    let result;
    await mutate((next) => {
      assertActive();
      if (next.restoration) { result = clone(next.restoration); return; }
      const latest = next.latest;
      const latestJob = next.jobs.find((job) => job.requestId === latest?.requestId) || next.jobs[next.jobs.length - 1];
      let restored;
      if (latestJob) {
        const previous = latest?.requestId === latestJob.requestId ? latest.task : latestJob.task;
        if (!latestJob.everSent && latestJob.status !== 'sending') {
          next.jobs = next.jobs.filter((job) => job.requestId !== latestJob.requestId);
          if (latest?.requestId === latestJob.requestId) next.latest = null;
          restored = { taskId: latestJob.taskId, task: clone(previous), localOnly: true, requestId: uuid(), undoRequestId: latestJob.dependsOnUndoRequestId || null };
          if (next.undo?.requestId === restored.undoRequestId) delete restored.task.undoToken;
        } else if (next.undo) {
          restored = { requestId: next.undo.requestId, undoRequestId: next.undo.requestId, taskId: next.undo.taskId, task: clone(next.undo.task || previous), localOnly: false };
        } else {
          const intentId = uuid();
          const previousTask = clone(previous); delete previousTask.undoToken;
          next.undo = { requestId: intentId, taskId: latestJob.taskId, task: previousTask, targetRequestId: latestJob.requestId, counted: countedAnswer(latestJob.answer), submissionId: null,
            status: latestJob.status === 'blocked' ? 'blocked' : 'queued', error: latestJob.status === 'blocked' ? 'Sync this saved review before Undo can finish. Its server status has not been confirmed.' : null, attempts: 0, nextAttemptAt: 0 };
          restored = { requestId: intentId, undoRequestId: intentId, taskId: latestJob.taskId, task: previousTask, localOnly: false };
        }
      } else if (next.undo) {
        restored = { requestId: next.undo.requestId, undoRequestId: next.undo.requestId, taskId: next.undo.taskId, task: clone(next.undo.task || { id: next.undo.taskId }), localOnly: false };
      } else {
        const receipt = next.lastSubmission;
        if (!receipt?.taskId || !receipt?.submissionId) throw failure('motion/outbox-undo', 'There is no saved motion review to undo.');
        const previous = clone(latest?.taskId === receipt.taskId ? latest.task : { id: receipt.taskId }); delete previous.undoToken;
        const intentId = uuid();
        next.undo = { requestId: intentId, taskId: receipt.taskId, task: previous, targetRequestId: latest?.taskId === receipt.taskId ? latest.requestId : null, counted: latest?.taskId === receipt.taskId && latest.answer ? countedAnswer(latest.answer) : countedReceipt(receipt), submissionId: receipt.submissionId, status: 'queued', attempts: 0, nextAttemptAt: 0 };
        restored = { requestId: intentId, undoRequestId: intentId, taskId: receipt.taskId, task: previous, localOnly: false };
      }
      next.restoration = restored;
      result = clone(restored);
    });
    schedule();
    return result;
  };
  const retry = async () => {
    runtimeError = null;
    await mutate((next) => {
      for (const job of next.jobs) { if (job.status !== 'sending') job.status = 'queued'; job.nextAttemptAt = 0; job.error = null; }
      const latestJob = next.jobs.find((job) => job.requestId === next.latest?.requestId);
      if (latestJob) next.latest.status = latestJob.status;
      if (next.undo) { next.undo.status = 'queued'; next.undo.error = null; next.undo.nextAttemptAt = 0; }
    });
    schedule();
  };
  const cancelFailedUndo = async () => {
    assertActive();
    let result;
    await mutate((next) => {
      assertActive();
      if (next.undo?.status !== 'blocked' || next.undo.errorCode !== 'failed-precondition' || !next.undo.definitive) {
        throw failure('motion/outbox-undo', 'Only an Undo that the server definitively refused can be cancelled. Pending requests must finish syncing first.');
      }
      const discarded = next.jobs.filter((job) => job.dependsOnUndoRequestId === next.undo.requestId);
      result = { canceledTaskId: next.undo.taskId, discardedTaskIds: [...new Set(discarded.map((job) => job.taskId))], discardedCount: discarded.length, canceledRestoreRequestId: next.restoration?.undoRequestId === next.undo.requestId ? next.restoration.requestId : null,
        notice: discarded.length ? 'The previous answer was kept. Its unsent replacement was discarded; your other answers are still saved.' : 'The previous answer was kept. Your other answers are still saved.' };
      next.jobs = next.jobs.filter((job) => job.dependsOnUndoRequestId !== next.undo.requestId);
      if (discarded.some((job) => job.requestId === next.latest?.requestId)) next.latest = null;
      if (next.restoration?.undoRequestId === next.undo.requestId) next.restoration = null;
      next.undo = null;
    });
    runtimeError = null; publish(); schedule();
    safelyCall(settings.onReconcile, null, { accepted: null, reason: 'undo-cancelled', ...result });
    return result;
  };
  const adoptServer = async ({ reviewedCount, lastSubmission, lastTask } = {}) => {
    await mutate((next) => {
      if (next.reviewedCount === null && Number.isFinite(reviewedCount)) next.reviewedCount = reviewedCount;
      if (next.jobs.length || next.undo || next.restoration) return;
      if (next.latest && (next.latest.taskId !== lastSubmission?.taskId || next.latest.receipt?.submissionId !== lastSubmission?.submissionId)) return;
      if (lastSubmission !== undefined) next.lastSubmission = lastSubmission || null;
      if (lastTask?.id && lastTask.id === next.lastSubmission?.taskId && next.lastSubmission.submissionId) {
        next.latest = { requestId: next.lastSubmission.submissionId, taskId: lastTask.id, task: taskSnapshot(lastTask), answer: null, status: 'acknowledged', receipt: next.lastSubmission };
      }
    });
  };
  // Returning to an undone clip must not keep its expired media links forever.
  // Only an authoritative buffer response may retire this local presentation;
  // an unfinished Undo or a replacement answer must remain intact.
  const reconcileRestoration = async ({ requestId, task: refreshedTask = null }) => {
    assertActive();
    const applied = await mutate((next) => {
      assertActive();
      const restored = next.restoration;
      if (!requestId || restored?.requestId !== requestId || next.undo) return false;
      if (next.jobs.some((job) => job.taskId === restored.taskId || (restored.undoRequestId && job.dependsOnUndoRequestId === restored.undoRequestId))) return false;
      if (refreshedTask) {
        if (refreshedTask.id !== restored.taskId || !refreshedTask.videoUrl) return false;
        restored.task = taskSnapshot(refreshedTask);
      } else {
        next.restoration = null;
      }
      return true;
    });
    if (applied) schedule();
    return applied;
  };
  const onStorage = (event) => { if (event.key === key) { refresh(); schedule(); } };
  const onOffline = () => { runtimeError = 'Offline. Your reviews are saved on this device and will sync when you reconnect.'; publish(); };
  const onOnline = () => {
    runtimeError = null;
    void mutate((next) => {
      for (const job of next.jobs) if (job.status !== 'blocked') { job.nextAttemptAt = 0; job.error = null; }
      if (next.undo?.status !== 'blocked' && next.undo) { next.undo.nextAttemptAt = 0; next.undo.error = null; }
    }).then(() => schedule()).catch(() => {});
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  refresh(); schedule();
  return {
    submit, undoLatest, retry, cancelFailedUndo, pump, refresh, adoptServer, reconcileRestoration,
    getSnapshot: () => snapshot,
    subscribe: (listener) => { listeners.add(listener); safelyCall(listener, snapshot); return () => listeners.delete(listener); },
    configure: (next) => { settings = { ...settings, ...next }; schedule(); },
    dispose: () => { disposed = true; clearTimeout(timer); window.removeEventListener('storage', onStorage); window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); publish(); listeners.clear(); },
  };
}

export function getMotionReviewOutbox(options) {
  const scope = String(options?.scope || '');
  if (!scope) throw failure('motion/outbox-scope', 'Sign in before saving a motion review.');
  let session = sessions.get(scope);
  if (!session) { session = createMotionReviewOutbox(options); sessions.set(scope, session); }
  else session.configure(options);
  return session;
}

export function resetMotionReviewOutboxSessions() {
  for (const session of sessions.values()) session.dispose();
  sessions.clear();
}
